import { Card, Flex, Grid, Modal, Stack, Text } from '@mantine/core';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTimer } from 'react-timer-hook';
import BadgeStatus from '../../components/elements/BadgeStatus';
import Button from '../../components/elements/Button';
import GridCell from '../../components/elements/GridCell';
import CustomerSearchForm from '../../components/forms/CustomerSearchForm';
import CustomerTypeList from '../../components/fragments/CustomerTypeList/index';
import IconActivatedUser from '../../components/icons/IconActivatedUser';
import IconBlockUser from '../../components/icons/IconBlockUser';
import IconInfo from '../../components/icons/IconInfo';
import IconSetting from '../../components/icons/IconSetting.js';
import IcUser from '../../components/icons/IcUser';
import { AppContext } from '../../contexts';
import { actionUpdateNikStatus, actionUpdateNikStatusBulk, clearRedux, fetchSearchNik } from './action';
import RenderModal from './modal';
import styles from './styles.scoped.css';
import { fetchSessionProtection } from '../../components/layouts/PageBase/actions.js';
import { IconHistoryFilled } from '../../components/icons/IconHistoryFilled.js';

export default function Component() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setBreadcrumbs } = useContext(AppContext);
  const [nationalityId, setNationalityId] = useState('');
  const [modalNikActivated, setModalNikActivated] = useState(false);
  const [UnableToActivateCustomerModal, setUnableToActivateCustomerModal] = useState(false);
  const [modalVariant, setModalVariant] = useState('reason');
  const [isChecked, setIsChecked] = useState(false);
  const [isTooManyRequests, setIsTooManyRequests] = useState(false);
  const dateNow = new Date();
  const { minutes, seconds, restart } =
    useTimer({
      expiryTimestamp: dateNow,
      onExpire: () => {
        dispatch(clearRedux('updateNikStatusBulk'));
        setIsTooManyRequests(false);
      },
      autoStart: false
    });
  const {
    dataNikManagement, updateNikStatus, updateNikStatusBulk
  } = useSelector((state) => state.nikManagement);

  const searchResult = dataNikManagement?.data || {};
  const content = [
    {
      title: 'Blokir NIK Pelanggan',
      description: 'Blokir lebih dari 1 NIK sekaligus dengan upload File',
      button: <>
        <IconBlockUser /> Blokir NIK Pelanggan
      </>,
      action: () => navigate('/customer-setting/nik-blocked-bulk'),
      secondButton: 'Status Proses Blokir Data',
      actionSecondButton: () => navigate('/customer-setting/status/block'),
    },
    {
      title: 'Aktifkan NIK Pelanggan',
      description: 'Aktifkan lebih dari 1 NIK sekaligus dengan upload File',
      button: <>
        <IconActivatedUser /> Aktifkan NIK Pelanggan
      </>,
      action: () => {
        navigate('/customer-setting/nik-activate-bulk');
      },
      secondButton: 'Status Proses Aktivasi Data',
      actionSecondButton: () => navigate('/customer-setting/status/unblock'),
    }
  ];

  useEffect(() => {
    dispatch(clearRedux('dataNikManagement'));
    setBreadcrumbs(['Blokir & Aktifkan NIK']);
    dispatch(fetchSessionProtection());
  }, []);

  useEffect(() => {
    if (updateNikStatus.success && !updateNikStatus.isLoading && modalNikActivated) {
      setModalNikActivated(false);
      setIsChecked(false);
      nationalityId && dispatch(fetchSearchNik(nationalityId, true));
    }
  }, [updateNikStatus]);

  useEffect(() => {
    if (updateNikStatusBulk.success && !updateNikStatusBulk.isLoading && modalNikActivated) {
      if (updateNikStatusBulk?.data?.failedTotal > 0) {
        setModalNikActivated(true);
        setModalVariant('reason');
      } else {
        setModalNikActivated(false);
      }
    } else if (!updateNikStatusBulk.success && updateNikStatusBulk.code === 400
      && updateNikStatusBulk.status === 'BAD_REQUEST'
      && updateNikStatusBulk?.data !== null
    ) {
      const newExpiry = new Date();
      newExpiry.setSeconds(newExpiry.getSeconds() + updateNikStatusBulk.data.countdownTime);
      restart(newExpiry);
      setIsTooManyRequests(true);
    } else if (!updateNikStatusBulk.success && !updateNikStatusBulk.isLoading) {
      setModalNikActivated(false);
      setIsChecked(false);
    }
  }, [updateNikStatusBulk]);

  const handleSubmit = (values) => {
    setNationalityId(values.nationalityId);
    dispatch(fetchSearchNik(values.nationalityId, true));
  };

  const handledNikActivatedBulk = (data) => {
    const formData = new FormData();
    formData.append('actionType', 'unblock');
    formData.append('file', data[0]);
    dispatch(actionUpdateNikStatusBulk(formData));
  };

  const handleSubmitSingle = (data) => {
    const formData = new FormData();
    formData.append('nationalityId', searchResult.nationalityIdPlan);
    formData.append('actionType', 'unblock');
    formData.append('reasonDetailMessage', data?.reason);
    formData.append('supportDocument', data.supportDocument || null);
    dispatch(actionUpdateNikStatus(formData));
  };

  const handledNikActivated = () => {
    const payload = {
      nationalityId: searchResult.nationalityIdPlan,
      actionType: 'unblock',
    };
    dispatch(actionUpdateNikStatus(payload));
  };

  const handleGoToDetail = () => {
    navigate(`/customer-setting/detail/${searchResult.nationalityIdPlan}`);
  };

  const handleGoToNikBlock = () => {
    navigate(`/customer-setting/nik-blocked/${searchResult.nationalityIdPlan}`);
  };

  const handledCloseModal = () => {
    setModalNikActivated(false);
    setIsChecked(false);
  };

  const _handleActivatedCustomer = () => {
    if (searchResult.isRequestUnblock) {
      setModalVariant('nikActivatedSingle');
      setIsChecked(false);
      setModalNikActivated(true);
    } else {
      setUnableToActivateCustomerModal(true);
    }
  };

  const renderResultSearch = () => {
    return (
      <Card mt={30} withBorder>
        <Flex align="center" gap="sm">
          <IcUser />
          <Text size="lg" weight={500}>{searchResult.name}</Text>
          <BadgeStatus label={searchResult.isBlocked ? 'NIK Diblokir' : 'Aktif'} />
        </Flex>
        <Stack mt="md">
          <GridCell data={searchResult.nationalityId} label="NIK" left={3} />
          <GridCell data={<CustomerTypeList customerTypes={searchResult?.customerTypes} />} label="Jenis Pelanggan" left={3} />
        </Stack>
        <Flex align="center" justify="space-between">
          <Button className={styles.btnAction} onClick={handleGoToDetail}>
            <IconInfo type={'filled'} /> Rincian Data
          </Button>
          {searchResult.isBlocked ? (
            <Button className={styles.btnAction}
              onClick={_handleActivatedCustomer}
              outlined style={'blue'}>
              <IconActivatedUser /> Aktifkan NIK Pelanggan
            </Button>
          ) : (
            <Button className={styles.btnAction} onClick={handleGoToNikBlock} style={'light-danger'}>
              <IconBlockUser /> Blokir NIK Pelanggan
            </Button>
          )}
        </Flex>
      </Card>
    );
  };
  return (
    <div>
      <Card px={24} radius="md" withBorder>
        <Flex align="center" justify="space-between" mb="md">
          <Text size="xl" weight={600}>Blokir dan Aktifkan Akun Pelanggan Sekaligus</Text>
          <Flex className={styles.flexSetting} gap={2} onClick={() => navigate('/customer-setting/setting-reason-block-id')}>
            <Text c={'#5E811E'} td="underline" underline>Pengaturan Alasan dan Keterangan Blokir NIK</Text>
            <IconSetting />
          </Flex>
        </Flex>
        <Grid>
          {content.map((item, index) => (
            <Grid.Col key={index} span={{ base: 6, md: 6, sm: 12 }}>
              <Card withBorder>
                <Text fw={500} size="lg">{item.title}</Text>
                <Text c={'#52575C'} fw={400} mb="md" size="md">{item.description}</Text>
                <Flex align="center" gap="sm">
                  <Button className={styles.btnAction} onClick={item.action} outlined style={'blue'}>
                    {item.button}
                  </Button>
                  <Button className={styles.btnStatusProcess} onClick={item.actionSecondButton} style={'primary-500'}>
                    <IconHistoryFilled/>
                    {item.secondButton}
                  </Button>
                </Flex>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      </Card>
      <Card mt={30} radius="md" withBorder>
        <Card.Section m="auto" maw="44rem" p={32} px={{ base: 20, md: 200 }}>
          <Flex direction={{ base: 'column', md: 'row' }} gap={'md'}>
            <img
              height="100"
              src="/assets/illu-setting-merchant.svg"
              width="100"
            />
            <div>
              <Text fw={500} fz={24}>Blokir atau Aktifkan NIK Pelanggan</Text>
              <Text c={'#52575C'} fw={400} mb="md" size="lg">
                Cari data NIK pelanggan untuk melakukan pemblokiran
                atau mengaktifkan kembali NIK pelanggan.
              </Text>
            </div>
          </Flex>
          <CustomerSearchForm
            handleSubmit={handleSubmit}
            isError={!dataNikManagement.success && dataNikManagement.message}
            isLoading={dataNikManagement.isLoading}
            message={dataNikManagement.message} />
          {(dataNikManagement.success && !dataNikManagement.isLoading) && renderResultSearch()}
        </Card.Section>
      </Card>
      <RenderModal
        actionType="unblock"
        data={updateNikStatusBulk}
        handledCloseModal={handledCloseModal}
        handledNikActivated={handledNikActivated}
        handleSubmitBulk={handledNikActivatedBulk}
        handleSubmitSingle={handleSubmitSingle}
        isChecked={isChecked}
        isLoading={updateNikStatusBulk.isLoading}
        isTooManyRequests={isTooManyRequests}
        minute={minutes}
        opened={modalNikActivated}
        second={seconds}
        variant={modalVariant}
      />
      <Modal
        centered
        onClose={() => setUnableToActivateCustomerModal(false)}
        opened={UnableToActivateCustomerModal}
        size={'lg'}
        title={'Tidak Dapat Mengaktifkan NIK Pelanggan'}
        withCloseButton={false}>
        <Stack spacing={8}>
          <Text c={'#52575C'} size={'lg'}>{searchResult?.reason || 'NIK pelanggan yang didaftarkan tidak valid.'}</Text>
          <Button onClick={() => setUnableToActivateCustomerModal(false)}>Tutup</Button>
        </Stack>
      </Modal>
    </div>
  );
}
